import { colorwayIndexRows } from '@apps/card/utils/colorway';
import type { ThemeEmbeddings } from '@shared/utils/search';
import { usePrecomputedEmbeddings } from '@shared/utils/search';
import { useMemo } from 'react';
import { colorFacetOptionForColorName } from './components/Filters/facetOptions';
import type { StationeryTemplate } from './DesignsGallery.types';

/**
 * One (template × colorway) pair whose palette matches a selectable color, e.g.
 * `boldly`'s `tomato` colorway matching `color:red`.
 */
export type ColorwayColorHit = Readonly<{
  templateId: string;
  themeId: string;
  colorwayId: string;
  /** Facet option this colorway satisfies, e.g. `color:red`. */
  colorOption: string;
  /** `0` when the color is the colorway's dominant color, `1` when its secondary. */
  rank: number;
  /** Position in the template's catalog colorway order; ties break on it. */
  optionIndex: number;
}>;

export const EMPTY_COLORWAY_ORDER: ReadonlyMap<string, ReadonlyArray<string>> = new Map();

/**
 * How many of a colorway's colors can match, taken in the index's prominence
 * order: the dominant (~75% by area) and the secondary (~20%). The ~5% accent is
 * too small a part of the card to justify surfacing it under that color, and
 * including it made near-every card match near-every color.
 *
 * A color's position within that pair is its `rank`, so among a theme's colorways
 * the one where the filtered color covers more of the card is preselected. Note
 * this is a narrower question than search's "what color is this card?", which is
 * why there is no equivalent of the white/beige promotion `colorSimilarity`
 * applies — demoting a neutral here would rank a white-dominant colorway below a
 * merely white-accented one under the White filter.
 */
const MATCHABLE_COLOR_COUNT = 2;

function colorHitsForColorway(
  template: StationeryTemplate,
  colorway: Readonly<{ colorwayId: string; optionIndex: number }>,
  colorNames: ReadonlyArray<string>,
): ColorwayColorHit[] {
  const hits: ColorwayColorHit[] = [];
  const matchedOptions = new Set<string>();

  colorNames.slice(0, MATCHABLE_COLOR_COUNT).forEach((colorName, rank) => {
    const colorOption = colorFacetOptionForColorName(colorName ?? '');
    // A colorway can hold the same color in both slots; iterating best-first
    // means the rank already recorded is the better one.
    if (!colorOption || matchedOptions.has(colorOption)) return;
    matchedOptions.add(colorOption);
    hits.push({ templateId: template.id, themeId: template.themeId, ...colorway, colorOption, rank });
  });

  return hits;
}

/**
 * Every colorway whose palette matches a selectable color, read off the
 * theme-search embeddings index: the index classifies each colorway row
 * (`boldly_tomato`) into the same fixed palette the `Colors` facet is built
 * from, which is what lets the filter see colorways at all — a template's own
 * `tags` describe one palette for the whole template.
 *
 * Only rows with a `colorwayId` are collected. A template with no colorways is
 * matched by its curated tags alone; taking its index colors too would silently
 * widen every color filter beyond what merchandisers tagged.
 */
export function collectColorwayColorHits(templates: ReadonlyArray<StationeryTemplate>, themes: Map<string, ThemeEmbeddings> | undefined): ColorwayColorHit[] {
  if (!themes) return [];

  return templates.flatMap(template =>
    colorwayIndexRows(template.themeId, template.themeJSON?.card?.front?.colorways, key => themes.has(key)).flatMap(row => {
      const colorNames = themes.get(row.indexKey)?.colorNames;
      // No `colorwayId`: the template has no colorways, so its curated tags decide.
      // No `colorNames`: the colorway is absent from the index (its coverage gap).
      if (!row.colorwayId || !colorNames?.length) return [];
      return colorHitsForColorway(template, { colorwayId: row.colorwayId, optionIndex: row.optionIndex }, colorNames);
    }),
  );
}

/** Template ids that have at least one matching colorway, per color facet option. */
export function templateIdsByColorOption(hits: ReadonlyArray<ColorwayColorHit>): ReadonlyMap<string, ReadonlySet<string>> {
  const result = new Map<string, Set<string>>();
  for (const hit of hits) {
    const ids = result.get(hit.colorOption) ?? new Set<string>();
    ids.add(hit.templateId);
    result.set(hit.colorOption, ids);
  }
  return result;
}

/**
 * `themeId` → matching colorway ids, most prominently-colored first, for the
 * selected color options. Shaped like the search-produced order (keyed by
 * `themeId`, since colorways are theme-level and identical across orientations)
 * so tiles consume both through one context.
 */
export function colorwayOrderForSelection(hits: ReadonlyArray<ColorwayColorHit>, selectedColorOptions: ReadonlySet<string>): ReadonlyMap<string, ReadonlyArray<string>> {
  if (!selectedColorOptions.size) return EMPTY_COLORWAY_ORDER;

  const matching = hits.filter(hit => selectedColorOptions.has(hit.colorOption));
  if (!matching.length) return EMPTY_COLORWAY_ORDER;

  // Dominant-color matches first, then catalog order — a deterministic ordering
  // (unlike search, there is no relevance score to sort on).
  const sorted = [...matching].sort((a, b) => a.rank - b.rank || a.optionIndex - b.optionIndex);

  const order = new Map<string, string[]>();
  for (const hit of sorted) {
    const colorwayIds = order.get(hit.themeId) ?? [];
    if (!colorwayIds.includes(hit.colorwayId)) colorwayIds.push(hit.colorwayId);
    order.set(hit.themeId, colorwayIds);
  }
  return order;
}

/**
 * Computed once per gallery render pass and shared by the filter (which
 * templates survive, which colorway each tile shows) and the facet counts, so
 * both report the same set. Empty until the embeddings index loads, at which
 * point the color facet widens from tags-only to colorway-aware.
 */
export function useColorwayColorHits(templates: ReadonlyArray<StationeryTemplate>): ReadonlyArray<ColorwayColorHit> {
  const { themes } = usePrecomputedEmbeddings();
  return useMemo(() => collectColorwayColorHits(templates, themes), [templates, themes]);
}
